import { useTranslations } from '@/lib/next-shim'
import { Expertise, ExpertiseFormSubmissions, ExpertisePerson } from '../lib/expertise-detail.types'

type ExpertiseStaff = Expertise['medecin']

type UseExpertiseDetailParams = {
  expertise: Expertise
  submissions: ExpertiseFormSubmissions
}

type PersonOptions = {
  label: string
  fallbackSpeciality: string
  namePrefix?: string
}

function toPerson(staff: ExpertiseStaff, { label, fallbackSpeciality, namePrefix = '' }: PersonOptions): ExpertisePerson | null {
  const info = staff?.userInfo
  if (!info) return null
  return {
    label,
    name: `${namePrefix}${info.name} ${info.surname}`,
    initials: `${info.name?.[0] ?? ''}${info.surname?.[0] ?? ''}`.toUpperCase() || null,
    speciality: staff?.speciality || fallbackSpeciality,
  }
}

/**
 * View-model for the expertise detail header, provider cards and forms section.
 * File and chat concerns live in `useExpertiseFiles` / `useExpertiseChat`.
 */
export function useExpertiseDetail({ expertise, submissions }: UseExpertiseDetailParams) {
  const t = useTranslations()

  const speciality = expertise.speciality || t('account.expertises.noSpeciality')
  const doctorLabel = t('account.expertises.doctor')

  // `medecin` is the expert answering the request; `createdBy` is the referring PDS — a doctor
  // (status 'MEDECIN') or a nurse (status 'PDS'), which drives both its label and the name title.
  const expert =
    toPerson(expertise.medecin, { label: doctorLabel, fallbackSpeciality: speciality, namePrefix: 'Dr. ' }) ??
    ({ label: doctorLabel, name: t('account.expertises.noSpeciality'), initials: null, speciality } satisfies ExpertisePerson)

  const referrerIsDoctor = expertise.createdBy?.status === 'MEDECIN'
  const pds = toPerson(expertise.createdBy, {
    label: t(referrerIsDoctor ? 'account.expertises.referringDoctor' : 'account.expertises.referringPDS'),
    fallbackSpeciality: speciality,
    namePrefix: referrerIsDoctor ? 'Dr. ' : '',
  })

  const formSubmissions = submissions.filter((submission) => submission.expertise?._id === expertise._id)

  return {
    statusText: t(`account.expertises.statuses.${expertise.status}`),
    speciality,
    expert,
    pds,
    formSubmissions,
  }
}
